/**
 * MarketingBadge Component
 * 
 * Displays customer marketing attribution and traffic source information.
 * Shows UTM parameters, referrer data, and customer journey context.
 * 
 * Features:
 * - UTM parameter display (source, medium, campaign)
 * - Traffic source visualization
 * - Page context and referrer information
 * - Customer journey insights
 */

import React from 'react'
import * as Types from '../../../types'
type CustomerProfileType = Types.CustomerProfile
import { 
  TrendingUp, 
  ExternalLink, 
  Search,
  Mail,
  Share2,
  MousePointer,
  Info,
  Globe
} from 'lucide-react'

export interface MarketingBadgeProps {
  /** Customer profile containing marketing attribution data */
  profile: CustomerProfileType
  /** Size variant for different use cases */
  size?: 'sm' | 'md' | 'lg'
  /** Show detailed tooltip on hover */
  showTooltip?: boolean
  /** Custom CSS classes */
  className?: string
}

/**
 * Get appropriate traffic source icon
 */
function getTrafficSourceIcon(source?: string) {
  if (!source) return <Globe className="w-3 h-3" />
  
  const lowerSource = source.toLowerCase()
  
  if (lowerSource.includes('google') || lowerSource.includes('search')) {
    return <Search className="w-3 h-3" />
  }
  
  if (lowerSource.includes('email') || lowerSource.includes('newsletter')) {
    return <Mail className="w-3 h-3" />
  }
  
  if (lowerSource.includes('social') || lowerSource.includes('facebook') || 
      lowerSource.includes('twitter') || lowerSource.includes('linkedin')) {
    return <Share2 className="w-3 h-3" />
  }
  
  if (lowerSource === 'direct') {
    return <MousePointer className="w-3 h-3" />
  }
  
  return <TrendingUp className="w-3 h-3" />
}

/**
 * Format traffic source for display
 */
function formatTrafficSource(profile: CustomerProfileType): string {
  const source = profile.utm_source || 'Direct'
  const medium = profile.utm_medium
  
  if (medium && medium !== source) {
    return `${source} ${medium}`
  }
  
  return source
}

/**
 * Format page context
 */
function formatPageContext(profile: CustomerProfileType): string {
  if (!profile.page_url) return 'Unknown page'
  
  try {
    const url = new URL(profile.page_url)
    return url.pathname === '/' ? 'Homepage' : url.pathname
  } catch {
    return profile.page_url
  }
}

/**
 * Get traffic source color scheme
 */
function getTrafficSourceColor(source?: string): { bg: string; text: string; border: string } {
  if (!source) return { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' }
  
  const lowerSource = source.toLowerCase()
  
  if (lowerSource.includes('google') || lowerSource.includes('search')) {
    return { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' }
  }
  
  if (lowerSource.includes('email')) {
    return { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' }
  }
  
  if (lowerSource.includes('social') || lowerSource.includes('facebook') || 
      lowerSource.includes('twitter') || lowerSource.includes('linkedin')) {
    return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' }
  }
  
  if (lowerSource === 'direct') {
    return { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' }
  }
  
  return { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' }
}

/**
 * MarketingBadge component for displaying customer marketing attribution
 */
export function MarketingBadge({
  profile,
  size = 'md',
  showTooltip = true,
  className = ''
}: MarketingBadgeProps) {
  
  const trafficSource = formatTrafficSource(profile)
  const pageContext = formatPageContext(profile)
  const icon = getTrafficSourceIcon(profile.utm_source)
  const colors = getTrafficSourceColor(profile.utm_source)
  
  // Size-based styling
  const sizeClasses = {
    sm: 'text-xs px-2 py-1 gap-1',
    md: 'text-sm px-3 py-1.5 gap-1.5',
    lg: 'text-base px-4 py-2 gap-2'
  }
  
  // Create detailed tooltip content
  const tooltipContent = showTooltip ? (
    <div className="space-y-1">
      <div className="font-medium">Marketing Attribution</div>
      <div className="text-xs space-y-0.5">
        <div><span className="font-medium">Source:</span> {profile.utm_source || 'Direct'}</div>
        {profile.utm_medium && (
          <div><span className="font-medium">Medium:</span> {profile.utm_medium}</div>
        )}
        {profile.utm_campaign && (
          <div><span className="font-medium">Campaign:</span> {profile.utm_campaign}</div>
        )}
        {profile.utm_term && (
          <div><span className="font-medium">Keyword:</span> {profile.utm_term}</div>
        )}
        {profile.referrer_url && (
          <div><span className="font-medium">Referrer:</span> {profile.referrer_url}</div>
        )}
        <div><span className="font-medium">Landing Page:</span> {pageContext}</div>
        {profile.page_title && (
          <div><span className="font-medium">Page Title:</span> {profile.page_title}</div>
        )}
      </div>
    </div>
  ) : null
  
  return (
    <div className="relative group">
      <div 
        className={`
          inline-flex items-center rounded-md font-medium
          ${colors.bg} ${colors.text} border ${colors.border}
          ${sizeClasses[size]}
          ${className}
        `}
        title={showTooltip ? undefined : `${trafficSource} • ${pageContext}`}
      >
        {/* Traffic source icon */}
        <span className="opacity-70">
          {icon}
        </span>
        
        {/* Source and page information */}
        <span className="truncate">
          {trafficSource} • {pageContext}
        </span>
        
        {/* Info icon for tooltip indicator */}
        {showTooltip && (
          <Info className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
        )}
      </div>
      
      {/* Detailed tooltip on hover */}
      {showTooltip && tooltipContent && (
        <div className="
          absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
          hidden group-hover:block z-50
          bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg
          min-w-max max-w-xs
          before:content-[''] before:absolute before:top-full before:left-1/2 
          before:transform before:-translate-x-1/2 before:border-4
          before:border-transparent before:border-t-gray-900
        ">
          {tooltipContent}
        </div>
      )}
    </div>
  )
}

/**
 * Compact marketing badge for small spaces
 */
export function CompactMarketingBadge({ profile }: { profile: CustomerProfileType }) {
  const source = profile.utm_source || 'Direct'
  const icon = getTrafficSourceIcon(source)
  const colors = getTrafficSourceColor(source)
  
  return (
    <div 
      className={`
        inline-flex items-center gap-1 text-xs rounded px-2 py-0.5
        ${colors.bg} ${colors.text}
      `}
      title={formatTrafficSource(profile)}
    >
      <span className="opacity-70">
        {icon}
      </span>
      <span className="truncate max-w-20">
        {source}
      </span>
    </div>
  )
}

/**
 * Campaign badge for highlighting specific campaigns
 */
export function CampaignBadge({ 
  campaign, 
  className = '' 
}: { 
  campaign?: string
  className?: string 
}) {
  if (!campaign) return null
  
  return (
    <div 
      className={`
        inline-flex items-center gap-1 text-xs bg-yellow-50 text-yellow-700 
        border border-yellow-200 rounded px-2 py-1 font-medium
        ${className}
      `}
      title={`Campaign: ${campaign}`}
    >
      <TrendingUp className="w-3 h-3" />
      <span className="truncate max-w-32">{campaign}</span>
    </div>
  )
}

/**
 * Traffic source summary for analytics
 */
export function TrafficSourceSummary({ profile }: { profile: CustomerProfileType }) {
  const hasUtmData = profile.utm_source || profile.utm_medium || profile.utm_campaign
  
  if (!hasUtmData && !profile.referrer_url) {
    return (
      <div className="text-xs text-gray-500">
        Direct traffic
      </div>
    )
  }
  
  return (
    <div className="space-y-1">
      {profile.utm_source && (
        <div className="text-xs">
          <span className="font-medium">Source:</span> {profile.utm_source}
        </div>
      )}
      
      {profile.utm_campaign && (
        <div className="text-xs">
          <span className="font-medium">Campaign:</span> {profile.utm_campaign}
        </div>
      )}
      
      {profile.referrer_url && !profile.utm_source && (
        <div className="text-xs">
          <span className="font-medium">Referrer:</span> {new URL(profile.referrer_url).hostname}
        </div>
      )}
    </div>
  )
}

export default MarketingBadge